// "הצעה לשינוי בתכנית" — the chat's approval card (Premium Coach design, 2026-10-25). Shows EXACTLY the change the
// backend prepared (every changed day: before -> after) and the only two actions: "אישור השינוי" writes that change,
// "לא עכשיו" drops it. Colours: the scoped Premium v2 names (.premium-v2), Graphite values in src/styles/theme.css.
import React from 'react';
import { ClipboardText, CaretLeft, CheckCircle, WarningCircle, FlagCheckered, UserCircle } from '@phosphor-icons/react';
import { profileSectionFor } from '../../utils/profileAction';

const FOCUS = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--p-accent)] focus-visible:outline-offset-2';
const QUALITY = /אינטרוול|טמפו|סף|מרוץ|VO2|עליות|פארטלק|ארוכה/;

function dm(iso) {
  const m = /^\d{4}-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? `${Number(m[2])}.${Number(m[1])}` : '';
}

function workoutText(w) {
  if (!w || w.rest) return 'מנוחה';
  return [w.label, w.km ? `${w.km} ק״מ` : null].filter(Boolean).join(' · ');
}

function Row({ c }) {
  const after = workoutText(c.after);
  const afterQuality = c.after && !c.after.rest && QUALITY.test(c.after.label || '');
  const extra = c.after && !c.after.rest ? [c.after.main_set, c.after.pace].filter(Boolean).join(' · ') : '';
  return (
    <li className="flex items-center gap-3 px-3 py-2.5" data-testid={`proposal-row-${c.date}`}>
      <span className="w-14 shrink-0 text-[13px] font-bold text-[var(--p-muted)] tabular-nums">
        {c.day_letter} {dm(c.date)}
      </span>
      <span className="flex-1 min-w-0 flex items-center gap-1.5 flex-wrap text-[14px]">
        <span className="line-through text-[var(--p-muted)]" aria-label={`במקום ${workoutText(c.before)}`}>{workoutText(c.before)}</span>
        <CaretLeft size={12} weight="bold" className="text-[var(--p-muted)] shrink-0" aria-hidden="true" />
        <span className={`font-bold ${afterQuality ? 'text-[var(--p-orange)]' : 'text-[var(--p-text)]'}`}>{after}</span>
        {extra && <span className="basis-full text-[12px] text-[var(--p-muted)]" dir="rtl"><bdi>{extra}</bdi></span>}
      </span>
    </li>
  );
}

// 2026-11-01: a day the request covers that needs no change (e.g. "ריצות קלות בלבד" on a day already easy) — shown so
// the athlete sees the whole request was covered. Nothing on it changes.
function UnchangedRow({ u }) {
  return (
    <li className="flex items-center gap-3 px-3 py-2.5" data-testid={`proposal-unchanged-${u.date}`}>
      <span className="w-14 shrink-0 text-[13px] font-bold text-[var(--p-muted)] tabular-nums">
        {u.day_letter} {dm(u.date)}
      </span>
      <span className="flex-1 min-w-0 flex items-center gap-1.5 flex-wrap text-[14px]">
        <span className="text-[var(--p-text)]">{workoutText(u.workout)}</span>
        <span className="text-[12px] text-[var(--p-muted)]">· ללא שינוי</span>
      </span>
    </li>
  );
}

const squash = (s) => String(s || '').replace(/[\s.,;:!?\-–—]+/g, ' ').trim();

// The coach's sentence right above the card already carries the reason (the backend builds it from the same `reason`
// when the model wrote none) -> the card does not repeat it.
export function reasonShownIn(reason, coachText) {
  const r = squash(reason);
  return r.length > 0 && squash(coachText).includes(r);
}

// onOpenRaces (optional): a blocked change on a race day points to the race list — navigation only.
// onOpenProfile (optional, 2026-10-30): a blocked change whose message names a profile setting (the backend's exact
// place phrase, utils/profileAction.js) opens the profile at that field's card — navigation only.
// coachText (optional): the coach's message right before the card.
export default function PlanProposalCard({ proposal, onApprove, onReject, onOpenRaces, onOpenProfile, coachText }) {
  const p = proposal || {};
  const status = p.status || 'processing';
  const profileSection = status === 'blocked' ? profileSectionFor(p.message) : null;
  const pending = status === 'pending' || status === 'applying' || status === 'approve_error';
  const busy = status === 'applying';
  const changes = Array.isArray(p.changes) ? p.changes : [];
  const unchanged = Array.isArray(p.unchanged) ? p.unchanged : [];
  // one list in date order: the changed days and the covered days that stay as they are
  const rows = [...changes.map((c) => ({ key: c.date, date: c.date, c })),
    ...unchanged.map((u) => ({ key: `u-${u.date}`, date: u.date, u }))]
    .sort((a, b) => String(a.date).localeCompare(String(b.date)));
  const showReason = p.reason && !reasonShownIn(p.reason, coachText);
  return (
    <div className="premium-v2 w-full max-w-[92%] rounded-[16px] border bg-[var(--p-card)] p-4" style={{ borderColor: "var(--g-accent)" }}
         data-testid="plan-proposal-card" data-status={status} role="group" aria-label="הצעה לשינוי בתכנית" dir="rtl">
      <div className="flex items-center gap-1.5 text-[13px] font-bold text-[var(--p-accent)]">
        <ClipboardText size={16} weight="bold" aria-hidden="true" />
        הצעה לשינוי בתכנית
      </div>

      {status === 'processing' && (
        // 2026-10-29: the preparation in progress — an indeterminate bar (no real progress data, so no percentages);
        // the same card becomes the approval card / the blocked or failed message when the status changes
        <div className="mt-3" role="status" aria-live="polite" data-testid="proposal-progress">
          <div className="text-[15px] font-semibold leading-snug text-[var(--p-text)]">המאמן מכין את השינוי...</div>
          <div className="mt-1 text-[13px] leading-snug text-[var(--p-muted)]">בודק את התאמת השינוי לתוכנית שלך.</div>
          <div className="mt-3 h-1.5 w-full rounded-full bg-[var(--p-inset)] overflow-hidden" role="progressbar"
               aria-label="המאמן מכין את השינוי..." aria-busy="true" data-testid="proposal-progressbar">
            <div className="proposal-indeterminate h-full w-1/3 rounded-full" style={{ background: 'var(--g-accent)' }}
                 data-testid="proposal-progress-fill" />
          </div>
        </div>
      )}

      {(pending || status === 'applied') && (
        <>
          {showReason && <p className="mt-2 mb-0 text-[15px] leading-snug text-[var(--p-text)]" data-testid="proposal-reason">{p.reason}</p>}
          <ul className="mt-3 mb-0 p-0 list-none rounded-[12px] bg-[var(--p-inset)] divide-y divide-[var(--p-inset-div)]"
              data-testid="proposal-changes">
            {rows.map((r) => (r.c ? <Row key={r.key} c={r.c} /> : <UnchangedRow key={r.key} u={r.u} />))}
          </ul>
          {Array.isArray(p.warnings) && p.warnings.length > 0 && (
            <div className="mt-3 flex gap-2 text-[13px] leading-snug text-[var(--p-orange)]" data-testid="proposal-warnings">
              <WarningCircle size={16} weight="bold" className="shrink-0 mt-0.5" aria-hidden="true" />
              <span>לתשומת לבך: {p.warnings.join(' ')}</span>
            </div>
          )}
        </>
      )}

      {pending && (
        <>
          {status === 'approve_error' && (
            <div className="mt-3 text-[13px] text-[var(--p-orange)]" role="alert">{p.error || 'לא הצלחתי לשמור כרגע — נסה שוב.'}</div>
          )}
          <div className="mt-3 flex gap-2">
            <button type="button" disabled={busy} onClick={onApprove} data-testid="proposal-approve"
                    className={`${FOCUS} flex-1 h-12 rounded-[12px] border-none btn-primary text-[15px] cursor-pointer`}>
              {busy ? 'שומר…' : 'אישור השינוי'}
            </button>
            <button type="button" disabled={busy} onClick={onReject} data-testid="proposal-reject"
                    className={`${FOCUS} w-[110px] h-12 rounded-[12px] bg-transparent border border-[var(--p-border)] text-[var(--g-sub)] text-[15px] font-bold cursor-pointer disabled:opacity-60`}>
              לא עכשיו
            </button>
          </div>
          <div className="mt-2 text-[12px] text-[var(--p-muted)]" data-testid="proposal-approval-note">
            השינוי יישמר בתוכנית רק לאחר שתאשר.
          </div>
        </>
      )}

      {status === 'applied' && (
        <div className="mt-3 flex items-center gap-1.5 text-[14px] font-bold text-[var(--p-done)]" role="status">
          <CheckCircle size={17} weight="fill" aria-hidden="true" />
          {p.message || 'התכנית עודכנה ונשמרה.'}
        </div>
      )}

      {!pending && status !== 'applied' && status !== 'processing' && (
        <div className="mt-2 text-[14px] leading-snug text-[var(--p-text)]" data-testid="proposal-message" role="status">
          {p.message || (status === 'rejected' ? 'ההצעה בוטלה — התכנית לא השתנתה.' : 'ההצעה אינה זמינה — התכנית לא השתנתה.')}
        </div>
      )}

      {status === 'blocked' && Array.isArray(p.alternatives) && p.alternatives.length > 0 && (
        <div className="mt-3" data-testid="proposal-alternatives">
          <div className="text-[13px] font-bold text-[var(--p-muted)]">מה כן אפשר:</div>
          <ul className="mt-1.5 mb-0 p-0 list-none rounded-[12px] bg-[var(--p-inset)] divide-y divide-[var(--p-inset-div)]">
            {p.alternatives.map((a) => (
              <li key={a} className="px-3 py-2 text-[14px] leading-snug text-[var(--p-text)]">{a}</li>
            ))}
          </ul>
          <div className="mt-1.5 text-[12px] text-[var(--p-muted)]">כתוב לי איזו אפשרות מתאימה לך, ואכין הצעה חדשה.</div>
        </div>
      )}

      {status === 'blocked' && onOpenRaces && /התחרויות שלי|לוח התחרויות/.test(p.message || '') && (
        <button type="button" onClick={onOpenRaces} data-testid="proposal-open-races"
                className={`${FOCUS} mt-3 h-11 px-4 rounded-full border border-border text-text-body text-[14px] font-semibold inline-flex items-center gap-2`}>
          <FlagCheckered size={16} weight="bold" aria-hidden="true" />
          מעבר ללוח התחרויות
        </button>
      )}

      {onOpenProfile && profileSection && (
        <button type="button" onClick={() => onOpenProfile(profileSection)} data-testid="proposal-open-profile"
                className={`${FOCUS} mt-3 h-11 px-4 rounded-full border border-border text-text-body text-[14px] font-semibold inline-flex items-center gap-2`}>
          <UserCircle size={16} weight="bold" aria-hidden="true" />
          מעבר לפרופיל
        </button>
      )}
    </div>
  );
}
